import { ChevronV2 } from 'assets/icons/ChevronV2'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { Input } from '../Form'
import { SelectOption, SelectOptionsContainer } from './Select.styles'

export type Option = { label: string; value: string | number | null }

export const Select = ({
  nullValue,
  options,
  onChange,
  width,
  placeholder,
  value,
  label,
  error,
}: {
  nullValue?: Option
  options: Option[]
  onChange: (value: Option) => void
  width?: string
  placeholder?: string
  value?: Option | null
  label?: string
  error?: string
}) => {
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setInputValue('')
        inputRef?.current?.blur()
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  const calculatePosition = () => {
    const rect = inputRef.current?.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const windowWidth = window.innerWidth
    const containerHeight = 200
    const containerWidth = 200
    if (!rect)
      return {
        top: 0,
        left: 0,
      }
    const position = {
      top: rect.top + 50,
      left: rect.left,
    }

    if (position.top + containerHeight > windowHeight) {
      return {
        bottom: windowHeight - rect.top + 10,
        left: rect.left,
      }
    }
    if (position.left + containerWidth > windowWidth) {
      position.left = rect.left - containerWidth
    }
    return {
      top: position.top,
      left: position.left,
    }
  }

  useEffect(() => {
    if (!open) {
      setInputValue('')
    }
  }, [open])

  useEffect(() => {
    inputRef.current?.addEventListener('focus', () => {
      setOpen(true)
    })
    inputRef.current?.addEventListener('blur', () => {
      setTimeout(() => {
        setOpen(false)
      }, 100)
    })
  }, [])

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(inputValue.toLowerCase()),
  )

  return (
    <Input
      width={width}
      label={label}
      placeholder={placeholder || nullValue?.label || ''}
      ref={inputRef}
      value={value?.label || inputValue}
      onChange={(e) => {
        setInputValue(e.target.value)
      }}
      error={error}
    >
      <ChevronIconContainer>
        <ChevronV2 open={open} />
      </ChevronIconContainer>
      {open &&
        createPortal(
          <SelectOptionsContainer
            style={{
              ...calculatePosition(),
            }}
            ref={dropdownRef}
          >
            {nullValue && (
              <SelectOption
                active={!value?.value}
                onMouseDown={() => {
                  onChange(nullValue)
                  setInputValue('')
                  setOpen(false)
                  inputRef?.current?.blur()
                }}
              >
                {nullValue.label}
              </SelectOption>
            )}
            {filteredOptions.map((option) => (
              <SelectOption
                key={option.value}
                active={option.value.toString() === value?.value?.toString()}
                onMouseDown={() => {
                  onChange(option)
                  setInputValue('')
                  setOpen(false)
                  inputRef?.current?.blur()
                }}
              >
                {option.label}
              </SelectOption>
            ))}
          </SelectOptionsContainer>,
          document.body,
        )}
    </Input>
  )
}

const ChevronIconContainer = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  height: 1.5rem;
  color: ${(props) => props.theme.textColor};
`
